import { useEffect, useState } from 'react'
import { api, ErroreApi } from '../servizi/api.ts'
import { useSessione } from '../servizi/sessione.tsx'
import { Attesa, Avviso } from './Pezzi.tsx'
import { Bottone, Campo, Riquadro } from './base.tsx'

/**
 * «Email di recupero» in «Il mio profilo» (decisioni di Davide dell'1/11/2026, 520).
 *
 * L'email personale dove arriva il collegamento per scegliere una password nuova, quando chi gestisce
 * gli utenti approva una richiesta di recupero. Non puo' essere un indirizzo di CIDIS: serve proprio
 * quando la casella CIDIS non si apre. Si cambia sempre con il codice di sei cifre mandato
 * all'indirizzo nuovo; quella di prima vale finche' il nuovo non e' confermato. La vede anche chi
 * gestisce gli utenti, che non la cambia.
 */

type Stato = {
  indirizzo: string | null
  verificata_il: string | null
  nuova: { indirizzo: string; dal: string } | null
  chiesta: boolean
}

const messaggio = (e: unknown) =>
  e instanceof ErroreApi ? e.message : 'Non è andata a buon fine: controlla la connessione e riprova.'

const giorno = (v: string) => new Date(v).toLocaleDateString('it-IT')

export function EmailRecupero() {
  const { aggiorna } = useSessione()
  const [stato, setStato] = useState<Stato | null>(null)
  const [scritta, setScritta] = useState('')
  const [codice, setCodice] = useState('')
  const [cambia, setCambia] = useState(false)
  const [errore, setErrore] = useState<string | null>(null)
  const [esito, setEsito] = useState<string | null>(null)
  const [inCorso, setInCorso] = useState(false)

  const carica = () => api.get<Stato>('/profilo/email-recupero').then(setStato)
  useEffect(() => { carica().catch((e) => setErrore(messaggio(e))) }, [])

  async function esegui(azione: () => Promise<void>) {
    setErrore(null); setEsito(null); setInCorso(true)
    try { await azione() } catch (e) { setErrore(messaggio(e)) } finally { setInCorso(false) }
  }

  const mandaCodice = () => esegui(async () => {
    const r = await api.post<{ indirizzo: string; email_inviata: boolean; motivo_email: string | null }>(
      '/profilo/email-recupero', { email: scritta.trim() })
    setCodice('')
    await carica()
    setCambia(false)
    setEsito(r.email_inviata
      ? `Ti abbiamo mandato un codice di sei cifre a ${r.indirizzo}: scrivilo qui sotto. Vale mezz’ora.`
      : `Il codice non è partito${r.motivo_email ? ` (${r.motivo_email})` : ''}. Riprova fra poco.`)
  })

  const conferma = () => esegui(async () => {
    const r = await api.post<{ indirizzo: string }>('/profilo/email-recupero/conferma', { codice })
    setScritta(''); setCodice('')
    await carica()
    await aggiorna()
    setEsito(`Fatto: ${r.indirizzo} è la tua email di recupero.`)
  })

  if (!stato) return errore ? <Avviso tipo="errore">{errore}</Avviso> : <Attesa testo="Caricamento…" />

  const inAttesa = stato.nuova
  const modulo = !stato.indirizzo || !stato.verificata_il || cambia

  return (
    <Riquadro titolo="Email di recupero">
      <p className="piccolo tenue">
        Se dimentichi la password, nella pagina di accesso premi «Ho dimenticato la password»: quando chi gestisce
        gli utenti approva la richiesta, il collegamento per sceglierne una nuova arriva qui. Usa un indirizzo
        personale, non quello di CIDIS. La vede anche chi gestisce gli utenti, ma la cambi solo tu.
      </p>
      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {esito && <Avviso tipo="successo">{esito}</Avviso>}

      {stato.indirizzo
        ? (
          <dl>
            <div className="dato">
              <dt>Email di recupero</dt>
              <dd>
                {stato.indirizzo}{' '}
                {stato.verificata_il
                  ? <span className="tenue">· verificata il {giorno(stato.verificata_il)}</span>
                  : <span className="tenue">· da verificare</span>}
              </dd>
            </div>
          </dl>
        )
        : stato.chiesta && !inAttesa && (
          <Avviso tipo="attenzione">Non hai ancora un’email di recupero: aggiungila qui sotto.</Avviso>
        )}

      {inAttesa && (
        <form onSubmit={(e) => { e.preventDefault(); void conferma() }} noValidate>
          <p className="nota-modulo">
            In attesa di conferma: {inAttesa.indirizzo}. Scrivi il codice che ti è arrivato lì.
          </p>
          <div className="campo-codice">
            <Campo etichetta="Codice ricevuto" valore={codice} maxLength={6} autoComplete="one-time-code"
                   modalitaTastiera="numeric" cambia={(v) => setCodice(v.replace(/\D/g, ''))} />
          </div>
          <div className="azioni-modulo">
            <Bottone variante="primario" tipo="submit" inCorso={inCorso} disabled={codice.length !== 6}>
              Conferma
            </Bottone>
          </div>
        </form>
      )}

      {modulo
        ? (
          <form onSubmit={(e) => { e.preventDefault(); void mandaCodice() }} noValidate>
            <Campo etichetta={stato.indirizzo ? 'Email di recupero nuova' : 'Email personale'} tipo="email"
                   modalitaTastiera="email" autoComplete="email" maxLength={254} valore={scritta} cambia={setScritta}
                   segnaposto="nome.cognome@gmail.com" />
            <div className="azioni-modulo">
              {cambia && <Bottone variante="discreto" onClick={() => setCambia(false)}>Annulla</Bottone>}
              <Bottone variante={inAttesa ? 'secondario' : 'primario'} tipo="submit" inCorso={inCorso}
                       disabled={!scritta.includes('@')}>
                {inAttesa ? 'Manda un codice nuovo' : 'Mandami il codice'}
              </Bottone>
            </div>
          </form>
        )
        : (
          <div className="azioni-modulo">
            <Bottone onClick={() => { setCambia(true); setEsito(null) }}>Cambia l’email di recupero</Bottone>
          </div>
        )}
    </Riquadro>
  )
}
